// 旧2ファイル形式（{castId}_btl.glb / {castId}_move.glb）の敵GLBを統合1ファイル形式
// （{castId}.glb・idle/walk 2クリップ）へ移行するための純関数群（2026-08-08 統一）。
// IndexedDB や fs に依存しないため Node のユニットテストでそのまま検証できる。
// 実行器はブラウザ版 browser/glb-migrate.mjs（プロジェクトを開いた時の無償・決定的移行）。

const GLB_MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a; // 'JSON'

function toU8(data) {
    if (data instanceof Uint8Array) return data;
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return null;
}

/**
 * GLB コンテナの JSON チャンクだけを読み、アニメーションを持つかを判定する。
 * フルパース不要（数MBのGLBでも即応答）。判定できない壊れたバイナリは null を返す。
 * @returns {boolean | null}
 */
export function glbHasAnimations(data) {
    const bytes = toU8(data);
    if (!bytes || bytes.byteLength < 20) return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(0, true) !== GLB_MAGIC) return null;
    let offset = 12;
    while (offset + 8 <= bytes.byteLength) {
        const chunkLength = view.getUint32(offset, true);
        const chunkType = view.getUint32(offset + 4, true);
        const dataStart = offset + 8;
        if (dataStart + chunkLength > bytes.byteLength) return null;
        if (chunkType === CHUNK_JSON) {
            try {
                const json = JSON.parse(new TextDecoder().decode(bytes.subarray(dataStart, dataStart + chunkLength)));
                return Array.isArray(json.animations) && json.animations.length > 0;
            } catch {
                return null;
            }
        }
        // チャンクは 4 バイト境界へパディングされる
        offset = dataStart + chunkLength + ((4 - (chunkLength % 4)) % 4);
    }
    return null;
}

/** バイト列の完全一致（旧フローの「同一モデルを _move / _btl へ複製」検出用）。 */
export function bytesEqual(a, b) {
    const ua = toU8(a);
    const ub = toU8(b);
    if (!ua || !ub || ua.byteLength !== ub.byteLength) return false;
    for (let i = 0; i < ua.byteLength; i++) {
        if (ua[i] !== ub[i]) return false;
    }
    return true;
}

/**
 * ペアの移行方法を決める。
 * - バイト同一（リグ無しの複製ペア）または btl にアニメが無い → 'copy'（btl をそのまま新名へ）
 * - btl がアニメを持つ → 'merge'（idle=btl / walk=move の2クリップ統合）
 */
export function classifyPair(btlData, moveData) {
    if (bytesEqual(btlData, moveData)) return 'copy';
    return glbHasAnimations(btlData) === true ? 'merge' : 'copy';
}

/**
 * models/enemies/ 配下のアセットキー一覧から移行プランを作る。
 * @param {string[]} keys assets/ 相対パス（例: newgames/g1/models/enemies/x_btl.glb）
 * @returns {Array<{ base: string, out: string, action: 'unify'|'rename'|'cleanup', btl?: string, move?: string, src?: string, deletes: string[] }>}
 */
export function planEnemyGlbMigration(keys) {
    const groups = new Map(); // dir+base -> { btl, move, unified }
    for (const key of keys) {
        const name = key.slice(key.lastIndexOf('/') + 1);
        if (!name.endsWith('.glb')) continue;
        const dir = key.slice(0, key.lastIndexOf('/') + 1);
        let base = null;
        let kind = 'unified';
        if (name.endsWith('_btl.glb')) { base = name.slice(0, -'_btl.glb'.length); kind = 'btl'; }
        else if (name.endsWith('_move.glb')) { base = name.slice(0, -'_move.glb'.length); kind = 'move'; }
        else base = name.slice(0, -'.glb'.length);
        if (!base) continue;
        const groupKey = dir + base;
        const group = groups.get(groupKey) ?? { dir, base };
        group[kind] = key;
        groups.set(groupKey, group);
    }

    const plan = [];
    for (const group of groups.values()) {
        const oldFiles = [group.btl, group.move].filter(Boolean);
        if (oldFiles.length === 0) continue; // 統合済みのみ＝移行不要
        const out = `${group.dir}${group.base}.glb`;
        if (group.unified) {
            // 統合ファイルが既にある（再生成済み等）→ 旧ペアの掃除だけ
            plan.push({ base: group.base, out, action: 'cleanup', deletes: oldFiles });
        } else if (group.btl && group.move) {
            plan.push({ base: group.base, out, action: 'unify', btl: group.btl, move: group.move, deletes: oldFiles });
        } else {
            // 片割れだけ残った異常形（通常発生しない）→ その1つを新名へ改名
            plan.push({ base: group.base, out, action: 'rename', src: oldFiles[0], deletes: oldFiles });
        }
    }
    return plan.sort((a, b) => a.out.localeCompare(b.out));
}

/** 移行済み base への旧形式参照（…/{base}_btl.glb / …/{base}_move.glb）を統合名へ置き換える。 */
function rewriteModelPath(value, gameId, bases) {
    if (typeof value !== 'string') return null;
    const m = value.match(/^((?:\.\.\/)*)assets\/newgames\/([^/]+)\/models\/enemies\/(.+?)_(?:btl|move)\.glb$/);
    if (!m || m[2] !== gameId || !bases.has(m[3])) return null;
    return `${m[1]}assets/newgames/${m[2]}/models/enemies/${m[3]}.glb`;
}

/**
 * enemies.json（_shared 敵マスタ）の modelPathIdle / modelPathWalk を統合パスへ書き換える。
 * @returns {number} 書き換えたフィールド数
 */
export function rewriteEnemyModelPaths(document, gameId, migratedBases) {
    if (!Array.isArray(document?.enemies)) return 0;
    const bases = new Set(migratedBases);
    let updated = 0;
    for (const enemy of document.enemies) {
        for (const field of ['modelPathIdle', 'modelPathWalk']) {
            const next = rewriteModelPath(enemy?.[field], gameId, bases);
            if (next !== null && next !== enemy[field]) {
                enemy[field] = next;
                updated++;
            }
        }
    }
    return updated;
}

/**
 * image-prompts/index.json の敵エントリの glbPath を統合パスへ書き換える。
 * @returns {number} 書き換えたエントリ数
 */
export function rewriteEnemyPromptIndex(indexDocument, gameId, migratedBases) {
    if (!Array.isArray(indexDocument?.prompts)) return 0;
    const bases = new Set(migratedBases);
    let updated = 0;
    for (const entry of indexDocument.prompts) {
        if (entry?.role !== 'enemy') continue;
        const next = rewriteModelPath(entry?.glbPath, gameId, bases);
        if (next !== null && next !== entry.glbPath) {
            entry.glbPath = next;
            updated++;
        }
    }
    return updated;
}

/** 対象ゲームの敵GLBアセットキー接頭辞（assetFiles の走査範囲）。 */
export function enemyGlbKeyPrefix(gameId) {
    return `newgames/${gameId}/models/enemies/`;
}
